import { Check, Mail, MapPin, Pencil, RotateCcw, Trash2, UserPlus, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAudit,
  useInvite,
  useRemoveMember,
  useResendInvitation,
  useRevokeInvitation,
  useTeam,
  useUpdateMemberAccess,
} from '../api/hooks';
import { Badge, Button, Card, ErrorNote, Field, Input, Loading, PageHeader, Panel } from '../components/ui';
import { useApiError } from '../i18n/useApiError';
import { DEFAULT_STAFF_PERMISSIONS, OPERATOR_PERMISSIONS, type OperatorPermission } from '../permissions';

type Member = NonNullable<ReturnType<typeof useTeam>['data']>['members'][number];
type Invitation = NonNullable<ReturnType<typeof useTeam>['data']>['invitations'][number];
type TeamLot = NonNullable<ReturnType<typeof useTeam>['data']>['lots'][number];

const permissionKey = (permission: OperatorPermission) => permission.replace('.', '_');

function PermissionPicker({
  value,
  onChange,
  disabled = false,
}: {
  value: OperatorPermission[];
  onChange: (next: OperatorPermission[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const toggle = (permission: OperatorPermission) => {
    onChange(
      value.includes(permission) ? value.filter((item) => item !== permission) : [...value, permission],
    );
  };

  return (
    <fieldset className="grid gap-2 sm:grid-cols-2">
      <legend className="mb-2 text-xs font-semibold">{t('team.permissions')}</legend>
      {OPERATOR_PERMISSIONS.map((permission) => (
        <label
          key={permission}
          className="flex min-h-14 cursor-pointer items-start gap-2 rounded-lg border border-hairline px-3 py-2.5 has-[:checked]:border-ink has-[:checked]:bg-[#f5f4ef]"
        >
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-black"
            checked={value.includes(permission)}
            disabled={disabled}
            onChange={() => toggle(permission)}
          />
          <span className="flex flex-col gap-0.5">
            <span className="text-xs font-semibold">
              {t(`team.permissionLabels.${permissionKey(permission)}`)}
            </span>
            <span className="text-[11px] leading-snug text-muted">
              {t(`team.permissionDescriptions.${permissionKey(permission)}`)}
            </span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

function PermissionSummary({ permissions }: { permissions: OperatorPermission[] }) {
  const { t } = useTranslation();
  return (
    <span className="text-[11px] text-faint">
      {permissions.length
        ? permissions.map((permission) => t(`team.permissionLabels.${permissionKey(permission)}`)).join(' · ')
        : t('team.noPermissions')}
    </span>
  );
}

function LotScopePicker({
  lots,
  value,
  onChange,
  disabled = false,
}: {
  lots: TeamLot[];
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const toggle = (lotId: string) => {
    onChange(value.includes(lotId) ? value.filter((id) => id !== lotId) : [...value, lotId]);
  };

  return (
    <fieldset className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <legend className="text-xs font-semibold">
          {t('team.lotScope')} · {t('team.lotCount', { selected: value.length, total: lots.length })}
        </legend>
        <div className="flex gap-1">
          <Button
            disabled={disabled || value.length === lots.length}
            onClick={() => onChange(lots.map((lot) => lot.id))}
          >
            {t('team.selectAllLots')}
          </Button>
          <Button disabled={disabled || value.length === 0} variant="ghost" onClick={() => onChange([])}>
            {t('team.clearLots')}
          </Button>
        </div>
      </div>
      {lots.length ? (
        <div className="grid gap-2 sm:grid-cols-2">
          {lots.map((lot) => (
            <label
              key={lot.id}
              className="flex min-h-14 cursor-pointer items-start gap-2 rounded-lg border border-hairline px-3 py-2.5 has-[:checked]:border-ink has-[:checked]:bg-surface-muted"
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-black"
                checked={value.includes(lot.id)}
                disabled={disabled}
                onChange={() => toggle(lot.id)}
              />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate text-xs font-semibold">{lot.name}</span>
                <span className="font-mono text-[11px] text-muted">{lot.code}</span>
              </span>
            </label>
          ))}
        </div>
      ) : (
        <Panel>{t('team.noLots')}</Panel>
      )}
    </fieldset>
  );
}

function LotScopeSummary({
  lotIds,
  lots,
  owner = false,
}: {
  lotIds: string[];
  lots: TeamLot[];
  owner?: boolean;
}) {
  const { t } = useTranslation();
  const names = lots.filter((lot) => lotIds.includes(lot.id)).map((lot) => lot.name);
  return (
    <span className="flex items-start gap-1 text-[11px] text-faint">
      <MapPin className="mt-0.5 shrink-0" size={12} />
      <span>{owner ? t('team.allLots') : names.join(' · ') || t('team.noLotsAssigned')}</span>
    </span>
  );
}

function MemberRow({ member, lots }: { member: Member; lots: TeamLot[] }) {
  const { t } = useTranslation();
  const describeError = useApiError();
  const update = useUpdateMemberAccess();
  const remove = useRemoveMember();
  const [editing, setEditing] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [permissions, setPermissions] = useState(member.permissions);
  const [lotIds, setLotIds] = useState(member.lotIds);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setError(null);
    try {
      if (lotIds.length === 0) return setError(t('team.lotRequired'));
      await update.mutateAsync({ id: member.id, permissions, lotIds });
      setEditing(false);
    } catch (cause) {
      setError(describeError(cause, 'team.permissionUpdateFailed'));
    }
  };

  const removeMember = async () => {
    setError(null);
    try {
      await remove.mutateAsync(member.id);
    } catch (cause) {
      setError(describeError(cause, 'team.removeFailed'));
    }
  };

  return (
    <div className="border-b border-hairline px-3 py-3 last:border-b-0">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-sm font-semibold">{member.name}</span>
          <span className="truncate text-[11px] text-muted">{member.email}</span>
          {member.role === 'owner' ? (
            <>
              <span className="text-[11px] text-faint">{t('team.ownerScope')}</span>
              <LotScopeSummary lotIds={member.lotIds} lots={lots} owner />
            </>
          ) : (
            <>
              <PermissionSummary permissions={member.permissions} />
              <LotScopeSummary lotIds={member.lotIds} lots={lots} />
            </>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Badge tone={member.role === 'owner' ? 'success' : 'neutral'}>
            {t(member.role === 'owner' ? 'team.owner' : 'team.staff')}
          </Badge>
          {member.role === 'staff' ? (
            <Button
              variant="ghost"
              title={t('team.editPermissions')}
              onClick={() => {
                setPermissions(member.permissions);
                setLotIds(member.lotIds);
                setEditing((value) => !value);
                setConfirmingRemove(false);
              }}
            >
              <Pencil size={14} />
            </Button>
          ) : null}
        </div>
      </div>

      {editing ? (
        <div className="mt-3 flex flex-col gap-3 border-t border-hairline pt-3">
          <PermissionPicker value={permissions} onChange={setPermissions} />
          <LotScopePicker lots={lots} value={lotIds} onChange={setLotIds} />
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="dark"
              disabled={permissions.length === 0 || lotIds.length === 0 || update.isPending}
              onClick={() => void save()}
            >
              <Check size={14} /> {t('common.save')}
            </Button>
            <Button onClick={() => setEditing(false)}>
              <X size={14} /> {t('common.cancel')}
            </Button>
            {confirmingRemove ? (
              <>
                <Button variant="dark" disabled={remove.isPending} onClick={() => void removeMember()}>
                  {t('team.confirmRemove')}
                </Button>
                <Button onClick={() => setConfirmingRemove(false)}>{t('common.cancel')}</Button>
              </>
            ) : (
              <Button variant="ghost" onClick={() => setConfirmingRemove(true)}>
                <Trash2 size={14} /> {t('team.remove')}
              </Button>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function PendingInvitationRow({ invitation, lots }: { invitation: Invitation; lots: TeamLot[] }) {
  const { t, i18n } = useTranslation();
  const describeError = useApiError();
  const resend = useResendInvitation();
  const revoke = useRevokeInvitation();
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resendNow = async () => {
    setError(null);
    setSent(false);
    try {
      await resend.mutateAsync({ id: invitation.id, locale: i18n.language.startsWith('zh') ? 'zh' : 'en' });
      setSent(true);
    } catch (cause) {
      setError(describeError(cause, 'team.resendFailed'));
    }
  };

  const revokeNow = async () => {
    setError(null);
    try {
      await revoke.mutateAsync(invitation.id);
    } catch (cause) {
      setError(describeError(cause, 'team.revokeFailed'));
    }
  };

  return (
    <div className="border-b border-hairline px-3 py-3 last:border-b-0">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-sm font-semibold">{invitation.name}</span>
          <span className="truncate text-[11px] text-muted">{invitation.email}</span>
          <PermissionSummary permissions={invitation.permissions} />
          <LotScopeSummary lotIds={invitation.lotIds} lots={lots} />
          <span className="text-[11px] text-faint">
            {t('team.expiresAt', { date: new Date(invitation.expiresAt).toLocaleDateString(i18n.language) })}
          </span>
        </div>
        <Badge tone="warning">{t('team.pending')}</Badge>
      </div>
      {error ? (
        <div className="mt-2">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {sent ? <p className="mt-2 text-xs text-success">{t('team.resent')}</p> : null}
      <div className="mt-2 flex flex-wrap gap-2">
        <Button disabled={resend.isPending} onClick={() => void resendNow()}>
          <RotateCcw size={14} /> {t('team.resend')}
        </Button>
        {confirmingRevoke ? (
          <>
            <Button variant="dark" disabled={revoke.isPending} onClick={() => void revokeNow()}>
              {t('team.confirmRevoke')}
            </Button>
            <Button onClick={() => setConfirmingRevoke(false)}>{t('common.cancel')}</Button>
          </>
        ) : (
          <Button variant="ghost" onClick={() => setConfirmingRevoke(true)}>
            <Trash2 size={14} /> {t('team.revoke')}
          </Button>
        )}
      </div>
    </div>
  );
}

/** Members, invitations, explicit permissions and the audit trail. */
export function TeamPage({ canEdit }: { canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const describeError = useApiError();
  const team = useTeam();
  const audit = useAudit();
  const invite = useInvite();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [permissions, setPermissions] = useState<OperatorPermission[]>(DEFAULT_STAFF_PERMISSIONS);
  const [lotIds, setLotIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSent(null);
    if (!name.trim()) return setError(t('team.nameRequired'));
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return setError(t('team.emailInvalid'));
    if (permissions.length === 0) return setError(t('team.permissionRequired'));
    if (lotIds.length === 0) return setError(t('team.lotRequired'));
    try {
      await invite.mutateAsync({
        name: name.trim(),
        email: email.trim(),
        permissions,
        lotIds,
        locale: i18n.language.startsWith('zh') ? 'zh' : 'en',
      });
      setSent(email.trim());
      setOpen(false);
      setName('');
      setEmail('');
      setPermissions(DEFAULT_STAFF_PERMISSIONS);
      setLotIds([]);
    } catch (cause) {
      setError(describeError(cause, 'team.inviteFailed'));
    }
  };

  if (team.isPending) return <Loading />;
  if (!canEdit || team.isError || !team.data) return <Panel>{t('team.ownerOnly')}</Panel>;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t('team.title')}
        hint={t('team.hint')}
        actions={
          <Button variant="primary" onClick={() => setOpen((value) => !value)}>
            <UserPlus size={15} /> {t('team.invite')}
          </Button>
        }
      />

      {sent ? (
        <Panel>
          <span className="flex items-center gap-2 text-sm font-semibold">
            <Mail size={15} /> {t('team.invitationSent', { email: sent })}
          </span>
        </Panel>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-[1fr_1.1fr]">
        <div className="flex flex-col gap-4">
          {open ? (
            <Card>
              <form className="flex flex-col gap-4" onSubmit={submit}>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label={t('team.name')}>
                    <Input
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      autoComplete="name"
                    />
                  </Field>
                  <Field label={t('team.email')}>
                    <Input
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      autoComplete="email"
                    />
                  </Field>
                </div>
                <PermissionPicker value={permissions} onChange={setPermissions} />
                <LotScopePicker lots={team.data.lots} value={lotIds} onChange={setLotIds} />
                {error ? <ErrorNote message={error} /> : null}
                <div className="flex gap-2">
                  <Button type="submit" variant="dark" disabled={invite.isPending}>
                    <Mail size={14} /> {t(invite.isPending ? 'team.sending' : 'team.sendInvite')}
                  </Button>
                  <Button type="button" onClick={() => setOpen(false)}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </form>
            </Card>
          ) : null}

          <section className="flex flex-col gap-2">
            <h2 className="text-[13px] font-bold">{t('team.members')}</h2>
            <div className="overflow-hidden rounded-lg border border-hairline bg-white">
              {team.data.members.map((member) => (
                <MemberRow key={member.id} member={member} lots={team.data.lots} />
              ))}
            </div>
          </section>

          <section className="flex flex-col gap-2">
            <h2 className="text-[13px] font-bold">{t('team.pendingInvitations')}</h2>
            {team.data.invitations.length ? (
              <div className="overflow-hidden rounded-lg border border-hairline bg-white">
                {team.data.invitations.map((invitation) => (
                  <PendingInvitationRow key={invitation.id} invitation={invitation} lots={team.data.lots} />
                ))}
              </div>
            ) : (
              <Panel>{t('team.noPendingInvitations')}</Panel>
            )}
          </section>
        </div>

        <div className="flex flex-col gap-3">
          <h3 className="text-[13px] font-bold">{t('team.auditLog')}</h3>
          <div className="flex flex-col gap-2.5 border-l-2 border-hairline pl-3.5">
            {audit.data?.entries.map((entry) => (
              <div key={entry.id} className="flex flex-col gap-0.5">
                <span className="text-[13px]">{entry.summary}</span>
                <span className="num text-[11px] text-faint">
                  {new Date(entry.createdAt).toLocaleString(i18n.language)} · {entry.actorName}
                </span>
              </div>
            ))}
            {audit.data?.entries.length === 0 ? (
              <p className="text-xs text-muted">{t('team.auditEmpty')}</p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
